import { hasYesOrNo, checkValue } from 'models/validate';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { DEFAULT_LATEST, THIS_MONTH } from 'constants/dateLimits';
import { LocationModel, ValidateJSModel } from 'forms/shared/types';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import { REQUIRED } from 'constants/errors';
import { counselingRecommendedByOptions } from 'constants/enums/substanceOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { SubstanceAlcoholVoluntaryCounselingItemModel } from '../types';

const substanceAlcoholVoluntaryCounselingItemValidator: ValidateJSModel<SubstanceAlcoholVoluntaryCounselingItemModel> = {
  TreatmentProviderName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { presence: true, hasValue: true };
  },
  TreatmentProviderAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.ProviderStillPractices?.value === 'Yes' && attributes?.TreatmentProviderAddressNotApplicable?.applicable !== false) {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: true,
            requireIsMilitaryDiplomaticAddress: true,
            requireMilitaryDiplomaticInstallationName: isSafeUnitedStates(value),
            requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          }
        };
      }

      return {};
    }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },
  TreatmentProviderTelephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return {
      presence: true,
      model: { validator: phoneModelValidator, requireNumber: true },
    };
  },
  CounselingDates: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        daterange: { earliest: setToStartOfMonth(options?.applicantBirthdate), latest: DEFAULT_LATEST },
      };
    }

    const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_ALCOHOL_TREATMENT_YEARS;

    const beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
    let useDuration = false;

    if (years && beyondRequired?.valid && typeof years === 'number') {
      beyondRequired.datetime = beyondRequired.datetime.minus({ years });
      useDuration = !value?.present && isSafeDateLater(beyondRequired, setToStartOfMonth(getSafeDateTime(value?.to)));
    }

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: useDuration ? beyondRequired.datetime : setToStartOfMonth(options?.applicantBirthdate),
        tooEarly: useDuration ? 'error.beforeDuration' : 'error.applicantMin',
        latest: DEFAULT_LATEST
      },
    };
  },
  CompletedTreatment: { presence: true, hasValue: { validator: hasYesOrNo } },
  NoCompletedTreatmentExplanation: (value, attributes) => {
    if (checkValue(attributes?.CompletedTreatment, 'No')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
  CompletedTreatmentComments: {},
  TreatmentProviderNameNotApplicable: {},
  PracticeName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ProviderStillPractices?.value === 'Yes' && attributes?.PracticeNameNotApplicable?.applicable !== false) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  PracticeNameNotApplicable: {},
  TreatmentProviderPersonName: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && attributes?.TreatmentProviderNameNotApplicable?.applicable !== false) {
      return {
        presence: true,
        model: {
          validator: pvqNameModelValidator,
          requireMiddleName: false
        }
      };
    }

    return {};
  },
  TreatmentProviderAddressNotApplicable: {},
  TreatmentProviderPhones: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ProviderStillPractices?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true
              },
            },
          },
        },
      };
    }
    return {};
  },
  TreatmentProviderEmails: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ProviderStillPractices?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false,
              },
            },
          },
        },
      };
    }
    return {};
  },
  ProviderStillPractices: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  RecommendedBy: (value, attributes, attributeName, options) => {
    if (!isPVQForm(options?.formType)) { return {}; }

    return {
      presence: true,
      array: {
        length: { minimum: 1, message: REQUIRED },
        validator: { inclusion: counselingRecommendedByOptions },
      },
    };
  },
  RecommendedByExplanation: (value, attributes, attributeName, options = {}) => {
    if (isPVQForm(options?.formType) && attributes?.RecommendedBy?.values?.includes('Other')) {
      return { presence: true, hasValue: true };
    }

    return {};
  },
};

export default substanceAlcoholVoluntaryCounselingItemValidator;
